import {
  isValidOpaqueRefreshToken,
  scheduleProactiveAccessRefresh,
  initProactiveRefreshLifecycle,
} from './auth';
import { trackGuestMergeComplete } from './guestFunnelAnalytics';
import { prefetchWorkspaceCompanies } from './workspaceCompanies';

const extractNfcUidFromReturnUrl = (returnUrl) => {
  if (!returnUrl || typeof returnUrl !== 'string') return null;
  const match = returnUrl.match(/^\/(?:nfc|display-card|my-display-card)\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const pendingCardSaveHasUid = (nfcUID) => {
  if (!nfcUID) return false;
  const pendingCardSave = localStorage.getItem('pendingCardSave');
  if (!pendingCardSave) return false;
  try {
    const parsed = JSON.parse(pendingCardSave);
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    return arr.some((c) => c && c.nfcUID === nfcUID);
  } catch {
    return false;
  }
};

/**
 * Persist tokens, run post-login side effects, and navigate after password or OAuth auth.
 */
export function completeAuthSession({
  authResponse,
  setAuth,
  navigate,
  location,
  showInfo,
  prefetchOnLogin,
  emailFallback,
}) {
  const data = authResponse?.data || authResponse || {};

  localStorage.setItem('token', data.accessToken);
  if (isValidOpaqueRefreshToken(data.refreshToken)) {
    localStorage.setItem('refreshToken', data.refreshToken.trim());
  } else {
    localStorage.removeItem('refreshToken');
  }
  localStorage.setItem('role', data.role);
  const email = data.user?.email || emailFallback;
  if (email) {
    localStorage.setItem('userEmail', email);
  }

  // Start before navigate so /home joins this request instead of waiting on a
  // second one after the provider notices the new token.
  try {
    const prefetch = prefetchWorkspaceCompanies();
    if (prefetch && typeof prefetch.catch === 'function') prefetch.catch(() => {});
  } catch {
    /* non-blocking */
  }

  scheduleProactiveAccessRefresh();
  initProactiveRefreshLifecycle();
  setAuth(true);

  if (typeof prefetchOnLogin === 'function') {
    prefetchOnLogin().catch(() => {});
  }

  import('../services/contactsService')
    .then(({ prefetchContacts }) => {
      try {
        const maybePromise = prefetchContacts(data.user?.id);
        if (maybePromise && typeof maybePromise.catch === 'function') {
          maybePromise.catch(() => {});
        }
      } catch {
        /* non-blocking */
      }
    })
    .catch(() => {});

  if (data.guestMergePending?.requiresMeetingLocationStep) {
    navigate('/guest/first-meeting', { replace: true, state: { fromAuth: true } });
    return;
  }

  const activationCardId = localStorage.getItem('activationCardId');
  const stateReturnUrl = location?.state?.returnUrl;
  const queryReturnUrl = new URLSearchParams(location?.search || '').get('returnUrl');
  const enterpriseInviteReturn = sessionStorage.getItem('veloxEnterpriseInviteReturn');
  const rawReturnUrl =
    stateReturnUrl || (queryReturnUrl ? decodeURIComponent(queryReturnUrl) : null) || enterpriseInviteReturn;
  let returnUrl =
    rawReturnUrl && typeof rawReturnUrl === 'string' && rawReturnUrl.startsWith('/') && !rawReturnUrl.startsWith('//')
      ? rawReturnUrl
      : null;
  const activateCard = location?.state?.activateCard === true || !!activationCardId;
  if (activationCardId && !returnUrl) {
    returnUrl = `/nfc/${encodeURIComponent(activationCardId)}`;
  }

  const stateWantsContacts =
    location?.state?.postAuthRedirect === 'contacts' || location?.state?.postAuthRedirect === '/contacts';
  const returnUrlNfcUid = extractNfcUidFromReturnUrl(returnUrl);
  const isNfcReturnUrl = !!returnUrlNfcUid;
  const hasMatchingPendingCard = pendingCardSaveHasUid(returnUrlNfcUid);
  const shouldSkipReturnUrlForNfc =
    !activateCard && ((stateWantsContacts && isNfcReturnUrl) || (isNfcReturnUrl && hasMatchingPendingCard));

  const guestMerge = data.guestMerge;
  if (guestMerge && typeof showInfo === 'function') {
    trackGuestMergeComplete(guestMerge);
    if (guestMerge.failed > 0) {
      try {
        sessionStorage.removeItem('velox_guest_merge_banner_dismissed');
        sessionStorage.setItem(
          'velox_guest_merge_notice',
          JSON.stringify({
            v: 1,
            failed: guestMerge.failed,
            pendingRetry: !!guestMerge.pendingRetry,
            at: Date.now(),
          })
        );
      } catch {
        /* ignore */
      }
    }
    if (guestMerge.imported > 0 && guestMerge.failed > 0) {
      showInfo(
        `We added ${guestMerge.imported} contact${guestMerge.imported === 1 ? '' : 's'} from your save link, but ${guestMerge.failed} couldn’t finish syncing. Nothing was deleted — try signing in again later with the same email.`
      );
    } else if (guestMerge.imported > 0) {
      showInfo(
        `${guestMerge.imported} contact${guestMerge.imported === 1 ? '' : 's'} from your save link ${guestMerge.imported === 1 ? 'was' : 'were'} added to your list.`
      );
    } else if (guestMerge.failed > 0) {
      showInfo(
        `Some saves from your email link didn’t finish syncing (${guestMerge.failed}). Nothing was deleted — try signing in again later with the same email.`
      );
    }
  }

  if (shouldSkipReturnUrlForNfc) {
    navigate('/contacts', { replace: true, state: { fromAuth: true, fromNfc: true } });
  } else if (returnUrl) {
    navigate(returnUrl, { state: { fromAuth: true, ...(activateCard ? { activateCard: true } : {}) } });
  } else if (activationCardId) {
    navigate('/contacts', { state: { fromAuth: true } });
  } else {
    navigate('/home', { state: { fromAuth: true } });
  }
}

export { extractNfcUidFromReturnUrl, pendingCardSaveHasUid };
